/* Feeder → masters (Ale rulings 2026-09-03, 16a–16d).
 *
 * A FEEDER fund holds MASTER funds. Each master is a collective vehicle —
 * a fund with its OWN record and chain. The backend returns the feeder's
 * OWN legs only: a master appears as a leaf issuer entity (entity_nature
 * 'collective_vehicle'); the chain walker does NOT recurse into the
 * master's own chain, and there is no per-master headline endpoint
 * (verified against src/nolos 2026-09-03). So the feeder view is driven by
 * a MasterLink per master — the master's own fund id and its OWN
 * chain-status headline (proven / total, counted IN FULL, Art. 21) —
 * supplied by GET /api/funds/{id}/masters (backend follow-up; see BUILD.md).
 *
 *   16a  a master renders on the feeder's Overview as a fund box with its
 *        chain COLLAPSED behind a door; never expanded inline.
 *   16c  the feeder's chain status counts the masters IN FULL — every
 *        demand at every master, counted once, in the master's own figure
 *        — and the subjects (the feeder itself + one per master) SUM to the
 *        headline.
 *   16d  the master's demands are NEVER folded into the feeder's record
 *        numbers; the master's chain is a door beneath its fund-units row.
 *
 * Pure and import-free so the bare node runner can prove the reconciliation
 * — see tests/unit/masters.test.mjs. */

export interface MasterLink {
  /** The master's fund-head ENTITY id — the issuer on the feeder's leg. */
  entity_id: string
  /** The master's own fund id — the door leaves the feeder for this. */
  fund_id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  /** The master's OWN chain-status headline, counted in full (Art. 21):
   *  every demand at every level of the master's own chain. */
  proven: number
  total: number
  /** Target assets the master's own chain reaches. */
  targets: number
  /** Levels the master's own chain runs. */
  levels: number
  /** Open reconciliation breaks on the master's own record. */
  breaks: number
}

export interface Fraction {
  proven: number
  total: number
}

export interface MasterSubject {
  link: MasterLink
  proven: number
  total: number
}

export interface FeederStatus {
  /** The demands on everything the feeder reaches — the feeder itself plus
   *  every master counted in full. */
  overall: Fraction & { owed: number }
  /** The feeder's OWN demands: its entity, its positions in the masters,
   *  the fund-units instruments and the subscription trades. The master
   *  entities' own demands are NOT here — they are counted once, in each
   *  master's figure. */
  feeder: Fraction
  /** One per master, its OWN headline verbatim — counted IN FULL. */
  masters: MasterSubject[]
}

const isProven = (status: string): boolean => status === 'satisfied' || status === 'waived'

/** The feeder's OWN demands: the matrix rows whose object is NOT a master
 *  entity (the masters are counted once, via their own headline, so their
 *  entity's own demands must not also land here). */
export function feederItself(
  matrix: { object_id: string; status: string }[],
  masterEntityIds: Iterable<string>
): Fraction {
  const masters = new Set(masterEntityIds)
  let proven = 0
  let total = 0
  for (const r of matrix) {
    if (masters.has(r.object_id)) continue
    total += 1
    if (isProven(r.status)) proven += 1
  }
  return { proven, total }
}

/** THE FEEDER CHAIN STATUS (16c): the feeder itself + every master counted
 *  IN FULL; the subjects sum to the headline. */
export function feederChainStatus(feeder: Fraction, masters: MasterLink[]): FeederStatus {
  const subjects: MasterSubject[] = masters.map((link) => ({
    link,
    proven: link.proven,
    total: link.total,
  }))
  const proven = feeder.proven + subjects.reduce((n, m) => n + m.proven, 0)
  const total = feeder.total + subjects.reduce((n, m) => n + m.total, 0)
  return {
    overall: { proven, total, owed: total - proven },
    feeder,
    masters: subjects,
  }
}

/** THE RULE (16c): the subjects (the feeder itself + each master) sum to
 *  the headline, proven and total alike. */
export function reconciles(s: FeederStatus): boolean {
  const parts = [s.feeder, ...s.masters]
  const proven = parts.reduce((n, p) => n + p.proven, 0)
  const total = parts.reduce((n, p) => n + p.total, 0)
  return proven === s.overall.proven && total === s.overall.total
}

/** Every master is counted IN FULL — its figure equals its own headline,
 *  never scoped down by how deep the feeder chose to look (Art. 21: never
 *  scope down verification by chain depth). */
export function countedInFull(s: FeederStatus): boolean {
  return s.masters.every((m) => m.proven === m.link.proven && m.total === m.link.total)
}

/** Sum of the masters' figures — the "Through Master A and Master B: c of d"
 *  number on the feeder's status line. */
export function mastersTotal(s: FeederStatus): Fraction {
  return s.masters.reduce(
    (acc, m) => ({ proven: acc.proven + m.proven, total: acc.total + m.total }),
    { proven: 0, total: 0 }
  )
}

/** The masters a feeder's chain reaches: the legs whose issuer is a master
 *  (a fund head held as a collective vehicle). One entry per master, in
 *  first-seen chain order. */
export function mastersOnChain<S extends { issuer_id: string | null }>(
  chain: S[],
  links: MasterLink[]
): MasterLink[] {
  const byEntity = new Map(links.map((l) => [l.entity_id, l]))
  const seen = new Set<string>()
  const out: MasterLink[] = []
  for (const s of chain) {
    if (!s.issuer_id) continue
    const link = byEntity.get(s.issuer_id)
    if (link && !seen.has(link.entity_id)) {
      seen.add(link.entity_id)
      out.push(link)
    }
  }
  return out
}

/** The "through …" line under a fund-units row (16d): the master's own
 *  record — its headline, its breaks, its targets — as a door. The
 *  master's demands are counted once here and in the chain status, never
 *  folded into the feeder's row EV/RECO. */
export interface ThroughLine {
  fundId: string
  name: string
  proven: number
  total: number
  breaks: number
  targets: number
}

export function throughLine(link: MasterLink): ThroughLine {
  return {
    fundId: link.fund_id,
    name: link.name,
    proven: link.proven,
    total: link.total,
    breaks: link.breaks,
    targets: link.targets,
  }
}
